import { collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, runTransaction, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { CreatePagecallRoomRequest, CreatePagecallRoomResponse, GetExternalShareInfoRequest, GetExternalShareInfoResponse, GetExternalShareURLResponse, GetRoomInviteResponse, GetRoomItemDownloadURLResponse } from '../models/DTO';
import { ExternalShareLink, ExternalShareLinkDownload } from '../models/ExternalShareLink';
import { Room } from '../models/Room';
import { RoomItem, RoomItemType, UIRoomItem } from '../models/RoomItem';
import { GetPagecallRoomRequest, GetPagecallRoomResponse, GetPagecallUserRequest, GetPagecallUserResponse } from './../models/DTO';
import { FireClient, getFunc } from './FireClient';
import { Util, pojo, roomItemPojo } from './Utils/Util';

export class FireClientItem {
  constructor(private c: FireClient) {
  }

  subscribeToRecentRoomItems(roomId: string, handler: (items: RoomItem[]) => void) {
    const q = query(collection(this.c.store, Room.COL, roomId, RoomItem.COL),
      orderBy('pinned', 'desc'),
      orderBy('created', 'desc'),
      limit(200));

    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as RoomItem));
    });
  }

  subscribeToRoomItemsByType(roomId: string, types: RoomItemType[], handler: (uitems: RoomItem[]) => void) {
    const q = query(collection(this.c.store, Room.COL, roomId, RoomItem.COL),
      where('type', 'in', types),
      // orderBy('pinned', 'desc'),
      orderBy('created', 'desc'));

    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as RoomItem));
    });
  }

  async saveRoomItem(item: RoomItem) {
    const itemDoc = doc(this.c.store, Room.COL, item.roomId, RoomItem.COL, item.id);
    runTransaction(this.c.store, async (t) => {
      const existing = await t.get(itemDoc)
      if (existing.exists()) {
        const existingItem = existing.data() as RoomItem;
        item.isDataSource = existingItem.isDataSource;
      }
      await t.set(itemDoc, roomItemPojo(item));
    });
  }
  async saveRoomItems(items: RoomItem[]) {
    const batch = writeBatch(this.c.store);
    items.forEach(item => {
      const itemDoc = doc(this.c.store, Room.COL, item.roomId, RoomItem.COL, item.id);
      batch.set(itemDoc, roomItemPojo(item));
    });
    await batch.commit();
  }

  async delete(item: RoomItem) {
    await deleteDoc(doc(this.c.store, Room.COL, item.roomId, RoomItem.COL, item.id));
  }

  async deleteRoomItems(items: RoomItem[]) {
    const func = getFunc('deleteFile');

    const batch = writeBatch(this.c.store);
    items.forEach(item => {
      if (item.file) {
        const filepath = "/rooms/${item.roomId}/_file/${item.id}"
        func({ filepath });
      }
      const itemDoc = doc(this.c.store, Room.COL, item.roomId, RoomItem.COL, item.id);
      batch.delete(itemDoc);
    });
    await batch.commit();
  }

  async getRoomItems(roomId: string, types: RoomItemType[]) {
    const q = query(collection(this.c.store, Room.COL, roomId, RoomItem.COL),
      where('type', 'in', types),
      orderBy('created', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as RoomItem)
  }

  async getRoomItem(roomId: string, itemId: string): Promise<RoomItem | null> {
    const snap = await getDoc(doc(this.c.store, Room.COL, roomId, RoomItem.COL, itemId));
    return snap.exists() ? snap.data() as RoomItem : null;
  }

  // ============================== External Share Links ==============================

  subscribeToExternalShareLinks(roomId: string, handler: (links: ExternalShareLink[]) => void) {
    const q = query(collection(this.c.store, Room.COL, roomId, ExternalShareLink.COL), orderBy('updated', 'desc'));
    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as ExternalShareLink));
    });
  }

  subscribeToExternalShareDownloads(link: ExternalShareLink, handler: (links: ExternalShareLinkDownload[]) => void) {
    const q = query(collection(this.c.store, Room.COL, link.roomId, ExternalShareLink.COL, link.id, ExternalShareLinkDownload.COL), orderBy('created', 'desc'));
    return onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as ExternalShareLinkDownload));
    });
  }


  async getRoomInviteInfo(linkId: string): Promise<GetRoomInviteResponse | null> {
    const func = getFunc('getRoomInviteInfo');
    const res = await func({ linkId } as GetExternalShareInfoRequest);
    return res.data as GetRoomInviteResponse;
  }

  async saveExternalShareLink(link: ExternalShareLink) {
    await setDoc(doc(this.c.store, Room.COL, link.roomId, ExternalShareLink.COL, link.id), roomItemPojo(link));
  }

  async deleteExternalShareLink(link: ExternalShareLink) {
    await deleteDoc(doc(this.c.store, Room.COL, link.roomId, ExternalShareLink.COL, link.id));
  }

  async getExternalShareInfo(linkId: string, password: string | null): Promise<GetExternalShareInfoResponse> {
    const func = getFunc('getExternalShareInfo');
    const res = await func({ linkId, password } as GetExternalShareInfoRequest);
    return res.data as GetExternalShareInfoResponse;
  }

  async getExternalShareURL(linkId: string, itemId: string, password: string | null): Promise<GetExternalShareURLResponse> {
    const func = getFunc('getExternalShareURL');
    const res = await func({ linkId, itemId, password });
    return res.data as GetExternalShareURLResponse;
  }

  async getRoomItemDownloadURL(roomId: string, itemId: string): Promise<GetRoomItemDownloadURLResponse> {
    const func = getFunc('getRoomItemDownloadURL');
    const res = await func({ roomId, itemId });
    return res.data as GetRoomItemDownloadURLResponse;
  }

  // ============================== Pagecall ==============================

  async createPagecallRoom(data: CreatePagecallRoomRequest): Promise<CreatePagecallRoomResponse> {
    const func = getFunc('createPagecallRoom');
    const res = await func(pojo(data));
    return res.data as CreatePagecallRoomResponse;
  }

  async getPagecallRoom(data: GetPagecallRoomRequest): Promise<GetPagecallRoomResponse> {
    const func = getFunc('getPagecallRoom');
    const res = await func(pojo(data));
    return res.data as GetPagecallRoomResponse;
  }

  async getPagecallAccessToken(data: GetPagecallUserRequest): Promise<GetPagecallUserResponse> {
    const func = getFunc('getPagecallUser');
    const res = await func(pojo(data));
    return res.data as GetPagecallUserResponse;
  }

  async deletePagecallRoom(data: GetPagecallRoomRequest): Promise<boolean> {
    const func = getFunc('deletePagecallRoom');
    const res = await func(pojo(data));
    return res.data as boolean;
  }

  async setLockedBy(item: RoomItem, userId: string | null) {
    if (item.id)
      await updateDoc(doc(this.c.store, Room.COL, item.roomId, RoomItem.COL, item.id), { lockedByUserId: userId });
    else
      console.log("No item.id. Locked is skipped.");

  }

  async setIsDataSource(item: RoomItem, isDataSource: boolean) {
    await updateDoc(doc(this.c.store, Room.COL, item.roomId, RoomItem.COL, item.id), { isDataSource: isDataSource });
  }
}